import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, Paper, Typography } from '@material-ui/core'

import { UploadDocumentsModal } from '../uploadDocumentsModal/UploadDocumentsModal'

import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import { Button, DocumentsGallery, Spacer } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { usePermissions, useQuery } from '@percent/admin-dashboard/common/hooks'
import styles from '@percent/admin-dashboard/app/bankDetails/bankDetailsView/BankDetailsView.module.scss'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { BankDetailsDocumentErrorDialog } from '@percent/admin-dashboard/app/bankDetails/bankDetailsView/bankDetailsDocument/BankDetailsDocumentErrorDialog'
import { BankDetailStatus } from '@percent/domain/bank-details'

type BankDetailsDocumentsProps = {
  refreshTimeline: VoidFunction
  bankDetailsId: string
  bankDetailsStatus: BankDetailStatus
}

const canUploadDocumentStatuses = [BankDetailStatus.PENDING, BankDetailStatus.APPROVED]

export function BankDetailsDocuments({ refreshTimeline, bankDetailsId, bankDetailsStatus }: BankDetailsDocumentsProps) {
  const { t } = useTranslation()
  const { bankDetailsService } = useServices()
  const [openUploadDocumentsModal, setOpenUploadDocumentsModal] = useState(false)
  const bankDetailsReviewUploadDocumentsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_BANK_DETAILS_REVIEW_UPLOAD_DOCUMENTS
  )
  const internalFgaDoraBankDetailsPaymentMethodsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_BANK_DETAILS_PAYMENT_METHODS
  )
  const { userHasPermissions } = usePermissions()

  const canUploadDocument =
    !internalFgaDoraBankDetailsPaymentMethodsFeatureFlag ||
    userHasPermissions('bank_details_payment_methods:can_add_document')

  const [{ data: bankDetailsDocumentData, isLoading, errorMessage }, { refresh }] = useQuery(
    bankDetailsService.getBankDetailsDocumentsByBankDetailsId,
    { id: bankDetailsId }
  )

  const handleUploadDocumentsModal = () => {
    setOpenUploadDocumentsModal(prevState => !prevState)
  }

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <Grid container spacing={0} component={Paper} elevation={0} className={styles.listContainer}>
        <Grid item xs={12} className={styles.title}>
          <Typography variant="h6" data-testid="bank-details-documents-gallery">
            {t('typography.documents')}
          </Typography>
          {bankDetailsReviewUploadDocumentsFeatureFlag && canUploadDocumentStatuses.includes(bankDetailsStatus) && (
            <Button
              onClick={handleUploadDocumentsModal}
              type="button"
              variant="primary"
              data-testid="open-upload-documents-modal-button"
              size="small"
              disabled={!canUploadDocument}
            >
              {t('button.uploadDocuments')}
            </Button>
          )}
        </Grid>
        <Spacer axis="vertical" size="s" />
        <Grid item xs={12} className={styles.galleryContainer}>
          <DocumentsGallery
            files={bankDetailsDocumentData?.documents}
            withPreview={bankDetailsDocumentData?.documents?.length > 0}
            previewUrl="/document-preview"
          />
        </Grid>
      </Grid>
      <BankDetailsDocumentErrorDialog error={!!errorMessage} />
      <UploadDocumentsModal
        open={openUploadDocumentsModal}
        onClose={handleUploadDocumentsModal}
        refresh={refresh}
        refreshTimeline={refreshTimeline}
      />
    </>
  )
}
